import type { ModuleSearchResult } from '@/lib/api/search'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { useEntityTerminology } from '@/hooks/useEntityTerminology'
import { useFavorites } from '@/hooks/useFavorites'
import { usePageModuleContext } from '@/hooks/usePageModuleContext'
import { useSearchDropdown } from '@/hooks/useSearchDropdown'
import { searchApi } from '@/lib/api/search'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import {
  LINK_WORK_SUPPORTED_TYPES,
  normalizeLinkWorkTypes,
  type LinkWorkSupportedType,
} from '@/lib/utils/link-work-module-types'
import { getModuleLinkMeta } from '@/modules/module-link'
import { useOrganizationStore } from '@/store/organization.store'
import { useRecentItemsStore } from '@/store/recent-items.store'
import {
  CaretDownIcon,
  CircleNotchIcon,
  ClockCounterClockwiseIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  StarIcon,
  XIcon,
} from '@phosphor-icons/react'
import { createPortal } from 'react-dom'

import type { ModuleLink } from '@scrambl/shared'

const logger = createLogger('components/time-entry/LinkWorkSelector')

// Re-export so consumers can use ModuleSearchResult from here
export type { ModuleSearchResult } from '@/lib/api/search'

type LinkableModule = 'ALL' | LinkWorkSupportedType
type LinkableModuleType = Exclude<LinkableModule, 'ALL'>
const LINKABLE_MODULE_TYPES: LinkableModuleType[] = [
  ...LINK_WORK_SUPPORTED_TYPES,
]

// Session-persistent last used module (survives popover close, resets on page refresh)
let lastUsedModule: LinkableModule = 'ALL'

type LinkWorkValue = { type: string; id: string; name?: string }

const resolveSelectedModule = (
  options: LinkableModule[],
  preferred?: LinkableModule,
): LinkableModule => {
  if (preferred && options.includes(preferred)) return preferred
  if (options.includes('ALL')) return 'ALL'
  return options[0] ?? 'ALL'
}

const normalizeAllowedModuleTypes = (
  allowedModuleTypes?: string[],
): LinkableModuleType[] => {
  return normalizeLinkWorkTypes(allowedModuleTypes)
}

const matchesSearchScope = (
  item: {
    id: string
    type: string
    entityId?: string
    projectId?: string
  },
  searchScope?: {
    parentType: ModuleSearchResult['type']
    parentId: string
  },
) => {
  if (!searchScope) return true

  if (searchScope.parentType === 'ENTITY') {
    if (item.type === 'ENTITY') return item.id === searchScope.parentId
    if (item.entityId) return item.entityId === searchScope.parentId
    if (
      item.type === 'PROJECT' ||
      item.type === 'TASK' ||
      item.type === 'TASK_GROUP'
    ) {
      return item.entityId === searchScope.parentId
    }
    return false
  }

  if (searchScope.parentType === 'PROJECT') {
    if (item.type === 'PROJECT') return item.id === searchScope.parentId
    if (item.projectId) return item.projectId === searchScope.parentId
    if (item.type === 'TASK' || item.type === 'TASK_GROUP') {
      return item.projectId === searchScope.parentId
    }
    return false
  }

  if (searchScope.parentType === 'TASK') {
    return item.type === 'TASK' && item.id === searchScope.parentId
  }

  return true
}

export interface LinkWorkSelectorProps {
  value?: LinkWorkValue | ModuleLink | null
  onSelect: (result: ModuleSearchResult | null) => void
  onOpenSelected?: (value: LinkWorkValue) => void
  label?: string
  className?: string
  compact?: boolean
  disabled?: boolean
  closeOnScroll?: boolean
  defaultModule?: LinkableModule
  portalToBody?: boolean
  allowedModuleTypes?: string[]
  preserveValueWhileEditing?: boolean
  closeOnSelect?: boolean
  /** Focus the search input on mount (opens the recents dropdown). */
  autoFocus?: boolean
  searchScope?: {
    parentType: ModuleSearchResult['type']
    parentId: string
  }
}

export function LinkWorkSelector({
  value,
  onSelect,
  onOpenSelected,
  label,
  className,
  compact = false,
  disabled = false,
  closeOnScroll = true,
  defaultModule,
  portalToBody = true,
  allowedModuleTypes,
  preserveValueWhileEditing = false,
  closeOnSelect = false,
  autoFocus = false,
  searchScope,
}: LinkWorkSelectorProps) {
  // Resolve value from either format
  const resolvedValue = (() => {
    if (!value) return null
    if ('moduleType' in value && 'moduleId' in value) {
      return {
        type: value.moduleType,
        id: value.moduleId,
        name: value.moduleName ?? undefined,
      } as LinkWorkValue
    }
    return value as LinkWorkValue
  })()

  const allowedLinkableTypes = useMemo(
    () => normalizeAllowedModuleTypes(allowedModuleTypes),
    [allowedModuleTypes],
  )
  const selectableModuleTypes = useMemo(
    () =>
      allowedLinkableTypes.length > 0
        ? allowedLinkableTypes
        : LINKABLE_MODULE_TYPES,
    [allowedLinkableTypes],
  )
  const moduleSelectorOptions = useMemo<LinkableModule[]>(
    () =>
      selectableModuleTypes.length > 1
        ? (['ALL', ...selectableModuleTypes] as LinkableModule[])
        : [selectableModuleTypes[0]],
    [selectableModuleTypes],
  )

  const [isEditing, setIsEditing] = useState(
    !resolvedValue || !resolvedValue.name,
  )
  const [selectedModule, setSelectedModule] = useState<LinkableModule>(() =>
    resolveSelectedModule(
      moduleSelectorOptions,
      defaultModule ?? lastUsedModule,
    ),
  )
  const [moduleSelectorOpen, setModuleSelectorOpen] = useState(false)
  const [recentItems, setRecentItems] = useState<ModuleSearchResult[]>([])
  const [isLoadingRecent, setIsLoadingRecent] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)

  const { currentOrganization } = useOrganizationStore()
  const { addRecentItem } = useRecentItemsStore()
  const entityTerminology = useEntityTerminology()
  const pageContext = usePageModuleContext()

  const dropdownRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const {
    search,
    setSearch,
    setShowResults,
    isLoading,
    setIsLoading,
    searchResults,
    setSearchResults,
    dropdownPosition,
    inputRef,
    updateDropdownPosition,
  } = useSearchDropdown<ModuleSearchResult>({
    dropdownDataAttribute: 'data-link-work-dropdown',
    minSearchLength: 2,
    closeOnScroll,
  })

  // Favorites for the empty state dropdown (after search is initialized)
  const {
    favoriteEntities: favEntityItems,
    favoriteProjects: favProjectItems,
    favoriteModules,
    isFavorited,
    toggleFavorite,
  } = useFavorites()

  // Map module type to the favorites store type key (uppercase → lowercase for entity/project)
  const toFavoriteType = (type: string): string => {
    if (type === 'ENTITY') return 'entity'
    if (type === 'PROJECT') return 'project'
    return type // Other types use uppercase as-is in favoriteModules
  }

  // Normalize favoriteModule type to uppercase LinkableModuleType
  const normalizeFavModuleType = (type: string): string => {
    if (type === 'entity') return 'ENTITY'
    if (type === 'project') return 'PROJECT'
    return type
  }

  const handleToggleFavorite = useCallback(
    (item: ModuleSearchResult) => {
      const favType = toFavoriteType(item.type)
      toggleFavorite(item.id, favType, {
        name: item.name,
        parentName: item.parentName,
        identifier: item.identifier,
      })
    },
    [toggleFavorite],
  )

  // Build favorites from favoriteModules (single source of truth) + legacy items as fallback
  const favoriteItems: ModuleSearchResult[] =
    search.length < 2
      ? (() => {
          const items: ModuleSearchResult[] = []
          const seen = new Set<string>()

          // First: include all favoriteModules, normalized to uppercase types
          for (const m of favoriteModules) {
            const normalizedType = normalizeFavModuleType(m.type)
            if (
              !selectableModuleTypes.includes(
                normalizedType as LinkableModuleType,
              )
            )
              continue
            if (selectedModule !== 'ALL' && selectedModule !== normalizedType)
              continue
            const key = `${normalizedType}:${m.id}`
            if (seen.has(key)) continue
            seen.add(key)
            items.push({
              id: m.id,
              name: m.name,
              type: normalizedType as ModuleSearchResult['type'],
              relevanceScore: 1.0,
              parentName: m.parentName,
              identifier: m.identifier,
            })
          }

          // Fallback: include legacy entity/project favorites not already covered by favoriteModules
          if (
            selectableModuleTypes.includes('ENTITY') &&
            (selectedModule === 'ALL' || selectedModule === 'ENTITY')
          ) {
            for (const item of favEntityItems) {
              const key = `ENTITY:${item.id}`
              if (seen.has(key)) continue
              seen.add(key)
              items.push({
                id: item.id,
                name: item.name,
                type: 'ENTITY',
                relevanceScore: 1.0,
              })
            }
          }
          if (
            selectableModuleTypes.includes('PROJECT') &&
            (selectedModule === 'ALL' || selectedModule === 'PROJECT')
          ) {
            for (const item of favProjectItems) {
              const key = `PROJECT:${item.id}`
              if (seen.has(key)) continue
              seen.add(key)
              items.push({
                id: item.id,
                name: item.name,
                type: 'PROJECT',
                relevanceScore: 1.0,
                parentName: item.entityName,
              })
            }
          }

          return items
        })()
      : []

  // Track if dropdown should be shown (results or recent)
  const [showDropdown, setShowDropdown] = useState(false)
  const [isFavoritesCollapsed, setIsFavoritesCollapsed] = useState(false)
  const [isRecentCollapsed, setIsRecentCollapsed] = useState(false)

  useEffect(() => {
    const nextSelected = resolveSelectedModule(
      moduleSelectorOptions,
      selectedModule,
    )
    if (selectedModule !== nextSelected) {
      setSelectedModule(nextSelected)
    }
  }, [moduleSelectorOptions, selectedModule])

  const getApiTypesForModule = useCallback(
    (moduleType: LinkableModule): LinkableModuleType[] => {
      if (moduleType === 'ALL') return selectableModuleTypes
      return [moduleType]
    },
    [selectableModuleTypes],
  )

  // Focus on mount when requested — the focus event opens the recents
  // dropdown, so revealing the picker only takes one click.
  const autoFocusRef = useRef(autoFocus)
  useEffect(() => {
    if (!autoFocusRef.current) return
    const timer = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(timer)
  }, [inputRef])

  // Sync editing state when value changes
  const prevValueRef = useRef(resolvedValue)
  useEffect(() => {
    const prevValue = prevValueRef.current
    prevValueRef.current = resolvedValue
    const hadValue = !!prevValue
    const hasValue = !!resolvedValue
    if (hadValue !== hasValue) {
      setIsEditing(!hasValue)
    }
  }, [resolvedValue])

  // Get module label for display
  const getModuleLabel = useCallback(
    (moduleType: LinkableModule) => {
      if (moduleType === 'ALL') return 'All'
      if (moduleType === 'ENTITY') return entityTerminology.Singular
      if (moduleType === 'TASK_GROUP') return 'Task Group'
      const meta = getModuleLinkMeta(moduleType)
      return meta.label
    },
    [entityTerminology.Singular],
  )

  // Get placeholder text for the search input
  const getPlaceholder = useCallback(
    (moduleType: LinkableModule) => {
      if (moduleType === 'ALL') {
        const typeLabels = selectableModuleTypes.map(type => {
          if (type === 'ENTITY') return entityTerminology.plural.toLowerCase()
          if (type === 'TASK_GROUP') return 'task groups'
          return `${getModuleLinkMeta(type).label.toLowerCase()}s`
        })
        if (typeLabels.length === 1) {
          return `Search ${typeLabels[0]}...`
        }
        return `Search ${typeLabels.join(', ')}...`
      }
      if (moduleType === 'ENTITY')
        return `Search ${entityTerminology.plural}...`
      if (moduleType === 'TASK_GROUP') return 'Search task groups...'
      if (moduleType === 'PROJECT') return 'Search projects...'
      if (moduleType === 'EVENT') return 'Search events...'
      if (moduleType === 'ORGANIZATION') return 'Search organizations...'
      return 'Search tasks...'
    },
    [entityTerminology.plural, selectableModuleTypes],
  )

  // Fetch recent items for a given module (defaults to current selection)
  const fetchRecentItems = useCallback(
    async (moduleOverride?: LinkableModule) => {
      if (!currentOrganization?.id) return
      const moduleToFetch = moduleOverride ?? selectedModule
      setIsLoadingRecent(true)
      try {
        const items = await searchApi.getRecentItems({
          limit: 5,
          types: getApiTypesForModule(moduleToFetch),
          parentType: searchScope?.parentType,
          parentId: searchScope?.parentId,
        })
        setRecentItems(items)
      } catch (err) {
        logger.warn('Failed to fetch recent items:', err)
        setRecentItems([])
      } finally {
        setIsLoadingRecent(false)
      }
    },
    [
      currentOrganization?.id,
      selectedModule,
      getApiTypesForModule,
      searchScope?.parentType,
      searchScope?.parentId,
    ],
  )

  // Search effect — fires when search text or module changes
  useEffect(() => {
    if (search.length < 2) {
      setSearchResults([])
      setShowResults(false)
      setIsLoading(false)
      return
    }

    let cancelled = false
    const performSearch = async () => {
      setIsLoading(true)
      try {
        const searchTypes = getApiTypesForModule(selectedModule)
        const results = await searchApi.searchModules({
          q: search,
          limit: 20,
          types: searchTypes,
          preferredType: selectedModule === 'ALL' ? undefined : selectedModule,
          parentType: searchScope?.parentType,
          parentId: searchScope?.parentId,
        })
        const filteredResults =
          selectedModule === 'ALL'
            ? results
            : results.filter(result => result.type === selectedModule)
        if (!cancelled) {
          setSearchResults(filteredResults)
          setShowResults(true)
          setShowDropdown(true)
          updateDropdownPosition()
        }
      } catch (error) {
        logger.error('Search error:', error)
        if (!cancelled) {
          setSearchResults([])
          setShowResults(false)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    const timer = setTimeout(performSearch, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [
    search,
    selectedModule,
    selectableModuleTypes,
    getApiTypesForModule,
    searchScope?.parentType,
    searchScope?.parentId,
    setSearchResults,
    setShowResults,
    setIsLoading,
    updateDropdownPosition,
  ])

  // Build the context suggestion item
  const contextItem: ModuleSearchResult | null = (() => {
    if (!pageContext) return null
    if (!pageContext.moduleName) return null
    if (
      !selectableModuleTypes.includes(
        pageContext.moduleType as LinkableModuleType,
      )
    )
      return null
    // Show context when module selector is 'ALL' or matches the page type
    if (selectedModule !== 'ALL' && pageContext.moduleType !== selectedModule)
      return null
    const scopedContextItem = {
      id: pageContext.moduleId,
      name: pageContext.moduleName,
      type: pageContext.moduleType,
      relevanceScore: 1.0,
    }
    return matchesSearchScope(scopedContextItem, searchScope)
      ? scopedContextItem
      : null
  })()

  // Filtered favorites (excluding context item)
  const filteredFavoriteItems = favoriteItems.filter(item => {
    if (!matchesSearchScope(item, searchScope)) {
      return false
    }

    return !(
      contextItem &&
      item.id === contextItem.id &&
      item.type === contextItem.type
    )
  })

  const scopedRecentItems = recentItems.filter(item =>
    matchesSearchScope(item, searchScope),
  )
  const scopedSearchResults = searchResults.filter(item =>
    matchesSearchScope(item, searchScope),
  )

  // Build flat list for keyboard navigation: context + favorites + recent + search results
  const flatItems: ModuleSearchResult[] = (() => {
    const items: ModuleSearchResult[] = []
    if (contextItem) items.push(contextItem)
    if (search.length < 2) {
      // Show favorites then recent items when not searching
      for (const item of filteredFavoriteItems) {
        items.push(item)
      }
      for (const item of scopedRecentItems) {
        if (
          contextItem &&
          item.id === contextItem.id &&
          item.type === contextItem.type
        )
          continue
        // Skip if already in favorites
        if (
          filteredFavoriteItems.some(
            f => f.id === item.id && f.type === item.type,
          )
        )
          continue
        items.push(item)
      }
    } else {
      // Show search results
      for (const item of scopedSearchResults) {
        if (
          contextItem &&
          item.id === contextItem.id &&
          item.type === contextItem.type
        )
          continue
        items.push(item)
      }
    }
    return items
  })()

  // Auto-highlight when single result
  const prevFlatLengthRef = useRef(flatItems.length)
  useEffect(() => {
    if (flatItems.length !== prevFlatLengthRef.current) {
      prevFlatLengthRef.current = flatItems.length
      if (flatItems.length === 1) {
        setFocusedIndex(0)
      } else {
        setFocusedIndex(-1)
      }
    }
  }, [flatItems.length])

  // Handle module change
  const handleModuleChange = (moduleType: LinkableModule) => {
    setSelectedModule(moduleType)
    lastUsedModule = moduleType
    setModuleSelectorOpen(false)
    setSearch('')
    setSearchResults([])
    setShowResults(false)
    setRecentItems([])
    setFocusedIndex(-1)
    // Focus input and fetch recent for new module — pass moduleType directly
    // to avoid stale closure (selectedModule state hasn't updated yet)
    setTimeout(() => {
      inputRef.current?.focus()
      fetchRecentItems(moduleType)
    }, 0)
  }

  // Handle item selection
  const handleSelect = (result: ModuleSearchResult) => {
    const stayInPickerMode = !closeOnSelect && !resolvedValue

    // Track in recent items
    const trackableTypes = ['ENTITY', 'PROJECT', 'TASK', 'TASK_GROUP'] as const
    if (
      currentOrganization?.id &&
      trackableTypes.includes(result.type as (typeof trackableTypes)[number])
    ) {
      addRecentItem({
        id: result.id,
        name: result.name,
        type: result.type as
          | 'ENTITY'
          | 'PROJECT'
          | 'TASK'
          | 'TASK_GROUP'
          | 'CUSTOM_COLLECTION',
        identifier: result.identifier,
        parentName: result.parentName,
        organizationId: currentOrganization.id,
      })
    }

    onSelect(result)
    setSearch('')
    setSearchResults([])
    setShowResults(false)
    setFocusedIndex(-1)

    if (stayInPickerMode) {
      setIsEditing(true)
      setShowDropdown(false)
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
      return
    }

    setIsEditing(false)
    setShowDropdown(false)
  }

  const handleClear = () => {
    setIsEditing(true)
    setSelectedModule(
      resolveSelectedModule(
        moduleSelectorOptions,
        defaultModule ?? lastUsedModule,
      ),
    )
    setSearch('')
    setSearchResults([])
    setShowResults(false)
    setShowDropdown(false)
    setFocusedIndex(-1)
    if (!preserveValueWhileEditing) {
      onSelect(null)
    }
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleEdit = () => {
    setIsEditing(true)
    setShowResults(false)
    setShowDropdown(false)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  // Keyboard handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!showDropdown && flatItems.length > 0) {
        setShowDropdown(true)
        updateDropdownPosition()
      }
      setFocusedIndex(prev => (prev < flatItems.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (focusedIndex >= 0 && focusedIndex < flatItems.length) {
        handleSelect(flatItems[focusedIndex])
      } else if (flatItems.length === 1) {
        handleSelect(flatItems[0])
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      setShowDropdown(false)
      setShowResults(false)
      if (resolvedValue) {
        setIsEditing(false)
        setSearch('')
      }
    }
  }

  // Handle focus — show recent items
  const handleInputFocus = () => {
    updateDropdownPosition()
    if (search.length < 2) {
      setShowDropdown(true)
      fetchRecentItems()
    } else if (searchResults.length > 0) {
      setShowDropdown(true)
    }
  }

  // Handle blur — delay to allow click on dropdown items
  const handleInputBlur = () => {
    setTimeout(() => {
      const active = document.activeElement
      if (active?.closest('[data-link-work-dropdown]')) return
      if (active?.closest('[data-module-selector]')) return
      if (active?.closest('[data-radix-popper-content-wrapper]')) return
      setShowDropdown(false)
      setShowResults(false)
      setFocusedIndex(-1)
      if (preserveValueWhileEditing && resolvedValue) {
        setSearch('')
        setSearchResults([])
        setIsEditing(false)
      }
    }, 150)
  }

  useEffect(() => {
    if (!showDropdown) {
      return
    }

    const handlePointerDownOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (containerRef.current?.contains(target)) {
        return
      }

      if (dropdownRef.current?.contains(target)) {
        return
      }

      if ((target as Element | null)?.closest?.('[data-module-selector]')) {
        return
      }

      setShowDropdown(false)
      setShowResults(false)
      setFocusedIndex(-1)
      if (preserveValueWhileEditing && resolvedValue) {
        setSearch('')
        setSearchResults([])
        setIsEditing(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDownOutside)
    return () => {
      document.removeEventListener('mousedown', handlePointerDownOutside)
    }
  }, [
    preserveValueWhileEditing,
    resolvedValue,
    setSearch,
    setSearchResults,
    setShowResults,
    showDropdown,
  ])

  // Get module meta for rendering
  const getTypeMeta = (type: string) =>
    getModuleLinkMeta(type, { entityLabel: entityTerminology.singular })

  const hasDropdownContent =
    contextItem ||
    filteredFavoriteItems.length > 0 ||
    recentItems.length > 0 ||
    isLoadingRecent ||
    searchResults.length > 0 ||
    isLoading
  const shouldShowDropdown =
    isEditing &&
    showDropdown &&
    hasDropdownContent &&
    (portalToBody ? dropdownPosition.width > 0 : true)
  const shouldOpenInlineDropdownAbove = (() => {
    if (portalToBody || !containerRef.current) return false
    const rect = containerRef.current.getBoundingClientRect()
    const dropdownMaxHeight = 328
    const spaceBelow = window.innerHeight - rect.bottom
    return spaceBelow < dropdownMaxHeight && rect.top > spaceBelow
  })()

  // Determine section boundaries for rendering (for keyboard nav flatIdx)
  const contextEnd = contextItem ? 1 : 0
  const favoritesEnd = contextEnd + filteredFavoriteItems.length

  return (
    <div className={cn('space-y-2', className)}>
      {label && <Label>{label}</Label>}
      <div className="relative">
        {/* Selected value display */}
        {!isEditing && resolvedValue ? (
          <div
            className={cn(
              'group flex items-center rounded-lg border border-border/50 bg-muted/30 transition-colors hover:bg-muted/50',
              compact ? 'h-9 gap-2 px-2.5' : 'gap-3 px-3 py-2.5',
              onOpenSelected && !disabled && 'cursor-pointer',
            )}
            role={onOpenSelected ? 'button' : undefined}
            tabIndex={onOpenSelected && !disabled ? 0 : undefined}
            onClick={() => {
              if (!disabled && onOpenSelected) onOpenSelected(resolvedValue)
            }}
            onKeyDown={event => {
              if (
                !disabled &&
                onOpenSelected &&
                (event.key === 'Enter' || event.key === ' ')
              ) {
                event.preventDefault()
                onOpenSelected(resolvedValue)
              }
            }}
          >
            {resolvedValue.name ? (
              <>
                {(() => {
                  const typeMeta = getTypeMeta(resolvedValue.type)
                  const Icon = typeMeta.icon
                  return (
                    <div
                      className={cn(
                        'flex flex-shrink-0 items-center justify-center',
                        compact ? 'h-6 w-6 rounded-md' : 'h-8 w-8 rounded-lg',
                        typeMeta.bgClass,
                      )}
                    >
                      <Icon
                        className={cn(
                          compact ? 'h-3.5 w-3.5' : 'h-4 w-4',
                          typeMeta.textClass,
                        )}
                      />
                    </div>
                  )
                })()}
                <div className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block truncate font-medium',
                      compact ? 'text-[13px]' : 'text-sm',
                    )}
                  >
                    {resolvedValue.name}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div
                  className={cn(
                    'flex flex-shrink-0 items-center justify-center bg-muted/50',
                    compact ? 'h-6 w-6 rounded-md' : 'h-8 w-8 rounded-lg',
                  )}
                >
                  <CircleNotchIcon
                    className={cn(
                      compact ? 'h-3.5 w-3.5' : 'h-4 w-4',
                      'animate-spin text-muted-foreground',
                    )}
                  />
                </div>
                <span
                  className={cn(
                    'flex-1 text-muted-foreground',
                    compact ? 'text-[13px]' : 'text-sm',
                  )}
                >
                  Loading...
                </span>
              </>
            )}
            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  'rounded-md p-0 hover:bg-background/80',
                  compact ? 'h-6 w-6' : 'h-7 w-7',
                )}
                onClick={event => { event.stopPropagation(); handleEdit(); }}
                onPointerDown={event => event.stopPropagation()}
                onMouseDown={event => event.stopPropagation()}
                disabled={disabled}
                aria-label="Replace selected record"
                title="Replace selected record"
              >
                <PencilSimpleIcon
                  className={cn(
                    compact ? 'h-3 w-3' : 'h-3.5 w-3.5',
                    'text-muted-foreground',
                  )}
                />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  'rounded-md p-0 hover:bg-destructive/10 hover:text-destructive',
                  compact ? 'h-6 w-6' : 'h-7 w-7',
                )}
                onClick={event => { event.stopPropagation(); handleClear(); }}
                onPointerDown={event => event.stopPropagation()}
                onMouseDown={event => event.stopPropagation()}
                disabled={disabled}
                aria-label="Clear selected record"
                title="Clear selected record"
              >
                <XIcon className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
              </Button>
            </div>
          </div>
        ) : (
          /* Unified field: module selector + search input */
          <div
            ref={containerRef}
            className="flex items-center rounded-lg border border-border/50 bg-background transition-colors hover:border-border/80 focus-within:border-primary/60"
          >
            {/* Module selector — only shown when more than one type can be
                chosen; with a single allowed type the label is redundant. */}
            {moduleSelectorOptions.length > 1 && (
              <div data-module-selector>
                <DropdownMenu
                  open={moduleSelectorOpen}
                  onOpenChange={open => {
                    setModuleSelectorOpen(open)
                    if (open) {
                      setShowDropdown(false)
                      setShowResults(false)
                    }
                  }}
                >
                  <DropdownMenuTrigger asChild disabled={disabled}>
                    <button
                      type="button"
                      className={cn(
                        'flex items-center gap-1 whitespace-nowrap rounded-l-lg border-r border-border/50 px-2.5 py-2 text-xs font-medium transition-colors hover:bg-muted/50',
                        disabled && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      <span className="text-muted-foreground">
                        {getModuleLabel(selectedModule)}
                      </span>
                      <CaretDownIcon className="h-3 w-3 text-muted-foreground/60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    sideOffset={4}
                    className="min-w-[140px]"
                  >
                    {moduleSelectorOptions.map(moduleType => (
                      <DropdownMenuItem
                        key={moduleType}
                        className={cn(
                          selectedModule === moduleType &&
                            'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                        )}
                        onClick={() => handleModuleChange(moduleType)}
                      >
                        {moduleType !== 'ALL' &&
                          (() => {
                            const meta = getTypeMeta(moduleType)
                            const Icon = meta.icon
                            return (
                              <Icon
                                className={cn('h-3.5 w-3.5', meta.textClass)}
                              />
                            )
                          })()}
                        <span>{getModuleLabel(moduleType)}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Search input */}
            <div className="relative flex-1">
              <input
                ref={inputRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={getPlaceholder(selectedModule)}
                disabled={disabled}
                className={cn(
                  'w-full bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:outline-none',
                  disabled && 'cursor-not-allowed opacity-50',
                )}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
              />
              {isLoading && (
                <CircleNotchIcon className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>
          </div>
        )}

        {/* Dropdown */}
        {shouldShowDropdown &&
          (() => {
            const dropdownContent = (
              <div
                data-link-work-dropdown
                ref={dropdownRef}
                className={cn(
                  'overflow-hidden rounded-xl border border-border/50 bg-popover shadow-lg',
                  portalToBody
                    ? 'pointer-events-auto fixed z-[9999]'
                    : shouldOpenInlineDropdownAbove
                      ? 'absolute bottom-full left-0 right-0 z-50 mb-1'
                      : 'absolute left-0 right-0 top-full z-50 mt-1',
                )}
                style={
                  portalToBody
                    ? (() => {
                        const rect =
                          containerRef.current?.getBoundingClientRect()
                        if (!rect)
                          return {
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            width: `${dropdownPosition.width}px`,
                          }
                        const spaceBelow = window.innerHeight - rect.bottom
                        const dropdownMaxHeight = 328
                        const showAbove =
                          spaceBelow < dropdownMaxHeight &&
                          rect.top > spaceBelow
                        return showAbove
                          ? {
                              bottom: `${window.innerHeight - rect.top + 4}px`,
                              left: `${rect.left}px`,
                              width: `${rect.width}px`,
                            }
                          : {
                              top: `${rect.bottom + 4}px`,
                              left: `${rect.left}px`,
                              width: `${rect.width}px`,
                            }
                      })()
                    : undefined
                }
              >
                <div className="max-h-[320px] overflow-auto overscroll-contain">
                  {/* Context section */}
                  {contextItem && (
                    <div>
                      <div className="border-b border-border/50 px-3 py-1.5">
                        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                          Context
                        </span>
                      </div>
                      <div className="py-1">
                        <button
                          type="button"
                          className={cn(
                            'flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50',
                            focusedIndex === 0 && 'bg-accent',
                          )}
                          data-focused={focusedIndex === 0 || undefined}
                          ref={el => {
                            if (focusedIndex === 0 && el) {
                              el.scrollIntoView({ block: 'nearest' })
                            }
                          }}
                          onMouseDown={e => {
                            e.preventDefault()
                            handleSelect(contextItem)
                          }}
                        >
                          {(() => {
                            const meta = getTypeMeta(contextItem.type)
                            const Icon = meta.icon
                            return (
                              <div
                                className={cn(
                                  'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg',
                                  meta.bgClass,
                                )}
                              >
                                <Icon
                                  className={cn('h-4 w-4', meta.textClass)}
                                />
                              </div>
                            )
                          })()}
                          <div className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">
                              Link to this{' '}
                              {getModuleLabel(
                                contextItem.type as LinkableModule,
                              )}
                              : {contextItem.name}
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Favorites section — only when not actively searching */}
                  {search.length < 2 && filteredFavoriteItems.length > 0 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setIsFavoritesCollapsed(prev => !prev)}
                        className={cn(
                          'flex w-full items-center justify-between border-b border-border/50 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground',
                          contextItem && 'border-t',
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <StarIcon className="h-3 w-3" />
                          <span>Favorites</span>
                        </div>
                        <CaretDownIcon
                          className={cn(
                            'h-3 w-3 transition-transform',
                            isFavoritesCollapsed && '-rotate-90',
                          )}
                        />
                      </button>
                      {!isFavoritesCollapsed && (
                        <div className="py-1">
                          {filteredFavoriteItems.map((item, idx) => (
                            <DropdownItem
                              key={`favorite-${item.type}-${item.id}`}
                              item={item}
                              isFocused={focusedIndex === contextEnd + idx}
                              getTypeMeta={getTypeMeta}
                              onSelect={handleSelect}
                              entityLabel={entityTerminology.singular}
                              isFavorited={true}
                              onToggleFavorite={handleToggleFavorite}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recent items section — only when not actively searching */}
                  {search.length < 2 &&
                    (isLoadingRecent ||
                      scopedRecentItems.filter(
                        item =>
                          !(
                            contextItem &&
                            item.id === contextItem.id &&
                            item.type === contextItem.type
                          ) &&
                          !filteredFavoriteItems.some(
                            f => f.id === item.id && f.type === item.type,
                          ),
                      ).length > 0) && (
                      <div>
                        <button
                          type="button"
                          onClick={() => setIsRecentCollapsed(prev => !prev)}
                          className={cn(
                            'flex w-full items-center justify-between border-b border-border/50 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground',
                            (contextItem || filteredFavoriteItems.length > 0) &&
                              'border-t',
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <ClockCounterClockwiseIcon className="h-3 w-3" />
                            <span>
                              Recent{' '}
                              {selectedModule === 'ALL'
                                ? 'Items'
                                : selectedModule === 'ENTITY'
                                  ? entityTerminology.Plural
                                  : selectedModule === 'TASK_GROUP'
                                    ? 'Task Groups'
                                    : `${getModuleLinkMeta(selectedModule).label}s`}
                            </span>
                            {isLoadingRecent && (
                              <CircleNotchIcon className="h-3 w-3 animate-spin" />
                            )}
                          </div>
                          <CaretDownIcon
                            className={cn(
                              'h-3 w-3 transition-transform',
                              isRecentCollapsed && '-rotate-90',
                            )}
                          />
                        </button>
                        {!isRecentCollapsed &&
                          (isLoadingRecent && recentItems.length === 0 ? (
                            <div className="px-4 py-6 text-center">
                              <CircleNotchIcon className="mx-auto h-5 w-5 animate-spin text-muted-foreground/50" />
                            </div>
                          ) : (
                            <div className="py-1">
                              {scopedRecentItems
                                .filter(
                                  item =>
                                    !(
                                      contextItem &&
                                      item.id === contextItem.id &&
                                      item.type === contextItem.type
                                    ) &&
                                    !filteredFavoriteItems.some(
                                      f =>
                                        f.id === item.id &&
                                        f.type === item.type,
                                    ),
                                )
                                .map((item, idx) => {
                                  const flatIdx = favoritesEnd + idx
                                  return (
                                    <DropdownItem
                                      key={`recent-${item.type}-${item.id}`}
                                      item={item}
                                      isFocused={focusedIndex === flatIdx}
                                      getTypeMeta={getTypeMeta}
                                      onSelect={handleSelect}
                                      entityLabel={entityTerminology.singular}
                                      isFavorited={isFavorited(
                                        item.id,
                                        toFavoriteType(item.type),
                                      )}
                                      onToggleFavorite={handleToggleFavorite}
                                    />
                                  )
                                })}
                            </div>
                          ))}
                      </div>
                    )}

                  {/* Search results */}
                  {search.length >= 2 && (
                    <div>
                      {scopedSearchResults.length > 0 && (
                        <>
                          <div
                            className={cn(
                              'border-b border-border/50 px-3 py-1.5',
                              contextItem && 'border-t',
                            )}
                          >
                            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              <MagnifyingGlassIcon className="h-3 w-3" />
                              <span>
                                {selectedModule === 'ALL'
                                  ? 'Results'
                                  : selectedModule === 'ENTITY'
                                    ? entityTerminology.Plural
                                    : selectedModule === 'TASK_GROUP'
                                      ? 'Task Groups'
                                      : `${getModuleLinkMeta(selectedModule).label}s`}
                              </span>
                            </div>
                          </div>
                          <div className="py-1">
                            {scopedSearchResults
                              .filter(
                                item =>
                                  !(
                                    contextItem &&
                                    item.id === contextItem.id &&
                                    item.type === contextItem.type
                                  ),
                              )
                              .map((item, idx) => {
                                const flatIdx = contextEnd + idx
                                return (
                                  <DropdownItem
                                    key={`result-${item.type}-${item.id}`}
                                    item={item}
                                    isFocused={focusedIndex === flatIdx}
                                    getTypeMeta={getTypeMeta}
                                    onSelect={handleSelect}
                                    entityLabel={entityTerminology.singular}
                                    isFavorited={isFavorited(
                                      item.id,
                                      toFavoriteType(item.type),
                                    )}
                                    onToggleFavorite={handleToggleFavorite}
                                  />
                                )
                              })}
                          </div>
                        </>
                      )}
                      {!isLoading && scopedSearchResults.length === 0 && (
                        <div className="px-4 py-8 text-center">
                          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50">
                            <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground/50" />
                          </div>
                          <p className="mb-1 text-sm font-medium text-foreground">
                            No results found
                          </p>
                          <p className="mx-auto max-w-[240px] text-xs text-muted-foreground">
                            Try searching with different keywords
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Empty state — no recent items, no favorites, no search */}
                  {search.length < 2 &&
                    !isLoadingRecent &&
                    scopedRecentItems.length === 0 &&
                    !contextItem &&
                    filteredFavoriteItems.length === 0 && (
                      <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                        Search for{' '}
                        {selectedModule === 'ALL'
                          ? 'items'
                          : selectedModule === 'ENTITY'
                            ? entityTerminology.plural
                            : selectedModule === 'TASK_GROUP'
                              ? 'task groups'
                              : `${getModuleLinkMeta(selectedModule).label.toLowerCase()}s`}{' '}
                        to link
                      </div>
                    )}
                </div>
              </div>
            )

            if (portalToBody && typeof document !== 'undefined') {
              return createPortal(dropdownContent, document.body)
            }

            return dropdownContent
          })()}
      </div>
    </div>
  )
}

// Extracted dropdown item component for reuse
function DropdownItem({
  item,
  isFocused,
  getTypeMeta,
  onSelect,
  entityLabel,
  isFavorited,
  onToggleFavorite,
}: {
  item: ModuleSearchResult
  isFocused: boolean
  getTypeMeta: (type: string) => ReturnType<typeof getModuleLinkMeta>
  onSelect: (item: ModuleSearchResult) => void
  entityLabel: string
  isFavorited?: boolean
  onToggleFavorite?: (item: ModuleSearchResult) => void
}) {
  const meta = getTypeMeta(item.type)
  const Icon = meta.icon

  return (
    <button
      type="button"
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50',
        isFocused && 'bg-accent',
      )}
      data-focused={isFocused || undefined}
      ref={el => {
        if (isFocused && el) {
          el.scrollIntoView({ block: 'nearest' })
        }
      }}
      onMouseDown={e => {
        e.preventDefault()
        onSelect(item)
      }}
    >
      <div
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg',
          meta.bgClass,
        )}
      >
        <Icon className={cn('h-4 w-4', meta.textClass)} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{item.name}</span>
          {item.identifier && (
            <span
              className={cn(
                'flex-shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium',
                meta.bgClass,
                meta.textClass,
              )}
            >
              {item.identifier}
            </span>
          )}
        </div>
        {item.parentName && (
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className="truncate text-[11px] text-muted-foreground">
              {item.type === 'TASK_GROUP'
                ? 'Project'
                : item.type === 'TASK'
                  ? 'Project'
                  : entityLabel}
              : {item.parentName}
            </span>
          </div>
        )}
      </div>
      {onToggleFavorite && (
        <div
          role="button"
          tabIndex={-1}
          className="flex-shrink-0 rounded p-1 transition-colors hover:bg-muted"
          onMouseDown={e => {
            e.stopPropagation()
            e.preventDefault()
            onToggleFavorite(item)
          }}
        >
          <StarIcon
            className={cn(
              'h-3.5 w-3.5 transition-colors',
              isFavorited
                ? 'text-amber-500'
                : 'text-muted-foreground/40 hover:text-amber-400',
            )}
            weight={isFavorited ? 'fill' : 'regular'}
          />
        </div>
      )}
    </button>
  )
}
